import { useHandler } from "@redotech/react-util/hook";
import { BlockedPrintLabelGroup } from "@redotech/redo-merchant-fulfillment-rpc/schema/check-print-label-inventory";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

export interface InsufficientInventoryModalProps {
  open: boolean;
  printableCount: number;
  blockedGroups: BlockedPrintLabelGroup[];
  onConfirm: () => void;
  onPrintAll: () => void;
  onCancel: () => void;
}

export type OpenInsufficientInventoryModalParams = Omit<
  InsufficientInventoryModalProps,
  "open" | "onCancel"
>;

function shortfallText(group: BlockedPrintLabelGroup): string {
  return group.shortfalls
    .map((s) => `${s.sku} (${s.available} available, ${s.required} needed)`)
    .join(", ");
}

export const InsufficientInventoryModal = memo(
  function InsufficientInventoryModal({
    open,
    printableCount,
    blockedGroups,
    onConfirm,
    onPrintAll,
    onCancel,
  }: InsufficientInventoryModalProps) {
    const blockedCount = blockedGroups.length;
    const totalCount = printableCount + blockedCount;
    const primaryText =
      printableCount > 0
        ? `Print ${printableCount} fulfillable ${printableCount === 1 ? "order" : "orders"}`
        : "Print fulfillable orders";

    return (
      <RedoModal
        isOpen={open}
        modalSize="sm"
        onModalCloseRequested={onCancel}
        primaryButton={{
          text: primaryText,
          onClick: async () => {
            onConfirm();
          },
          disabled: printableCount === 0,
        }}
        secondaryButton={{
          text:
            totalCount === 1
              ? "Print this order anyway"
              : `Print all ${totalCount} anyway`,
          onClick: async () => {
            onPrintAll();
          },
        }}
        title="Not enough inventory to print all labels"
      >
        <Flex dir="column" gap="xl">
          <Text fontSize="md" fontWeight="thin" textColor="primary">
            {printableCount} of {totalCount}{" "}
            {totalCount === 1 ? "order" : "orders"} can be printed.{" "}
            {blockedCount}{" "}
            {blockedCount === 1 ? "order exceeds" : "orders exceed"} your
            available inventory and will be left in the table to print later
            once you restock:
          </Text>
          <Flex dir="column" gap="sm">
            {blockedGroups.map((group) => (
              <Text
                fontSize="sm"
                fontWeight="thin"
                key={group.fulfillmentGroupId}
                textColor="secondary"
              >
                {group.orderName}: {shortfallText(group)}
              </Text>
            ))}
          </Flex>
          <Text fontSize="xs" fontWeight="thin" textColor="secondary">
            Inventory reflects your latest stock on record and may not change
            until orders are fulfilled. If these numbers look off, you can print
            anyway.
          </Text>
        </Flex>
      </RedoModal>
    );
  },
);

export function useInsufficientInventoryModal() {
  const [state, setState] = useState<{
    open: boolean;
    printableCount: number;
    blockedGroups: BlockedPrintLabelGroup[];
    onConfirm: () => void;
    onPrintAll: () => void;
  }>({
    open: false,
    printableCount: 0,
    blockedGroups: [],
    onConfirm: () => {},
    onPrintAll: () => {},
  });

  const openInsufficientInventoryModal = useHandler(
    (params: OpenInsufficientInventoryModalParams) => {
      setState({ open: true, ...params });
    },
  );

  const insufficientInventoryModalProps: InsufficientInventoryModalProps =
    useMemo(
      () => ({
        open: state.open,
        printableCount: state.printableCount,
        blockedGroups: state.blockedGroups,
        onConfirm: () => {
          setState((s) => ({ ...s, open: false }));
          state.onConfirm();
        },
        onPrintAll: () => {
          setState((s) => ({ ...s, open: false }));
          state.onPrintAll();
        },
        onCancel: () => {
          setState((s) => ({ ...s, open: false }));
        },
      }),
      [state],
    );

  return { insufficientInventoryModalProps, openInsufficientInventoryModal };
}
